(() => {
  'use strict';

  const GLOBAL_KEY = '__modularPlanterAssemblyV2';

  const safeFilename = (value) => String(value || 'modular-planter')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 70) || 'modular-planter';

  const pdfText = (value) => String(value ?? '')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u00A0/g, ' ')
    .replace(/[^\x20-\x7E\xA0-\xFF\n]/g, '?');

  const formatMoney = (cents, currency) => {
    if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return 'Set in cart';
    try {
      return new Intl.NumberFormat('en-NZ', {
        style: 'currency',
        currency: currency || 'NZD'
      }).format(Number(cents) / 100);
    } catch (error) {
      return `${currency || 'NZD'} ${(Number(cents) / 100).toFixed(2)}`;
    }
  };

  const getBounds = (points) => {
    const xs = points.map((point) => Number(point.x));
    const ys = points.map((point) => Number(point.y));
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys)
    };
  };

  const requireJsPdf = () => {
    const JsPdf = window.jspdf?.jsPDF;
    if (!JsPdf) {
      throw new Error('The bundled jsPDF asset has not loaded.');
    }
    return JsPdf;
  };

  const setFont = (doc, style = 'normal', size = 10, colour = [32, 32, 32]) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(...colour);
  };

  const drawSectionHeading = (doc, text, y) => {
    setFont(doc, 'bold', 14);
    doc.text(pdfText(text), 14, y);
    doc.setDrawColor(32, 32, 32);
    doc.setLineWidth(0.6);
    doc.line(14, y + 2.5, 196, y + 2.5);
    return y + 8;
  };

  const addPageHeader = (doc, data, title) => {
    setFont(doc, 'normal', 8, [100, 100, 100]);
    doc.text(pdfText(data.shopName || ''), 14, 10);
    setFont(doc, 'bold', 11);
    doc.text(pdfText(title), 14, 16);
    doc.setDrawColor(215, 215, 210);
    doc.line(14, 19, 196, 19);
    return 26;
  };

  const ensureSpace = (doc, data, y, height, title) => {
    if (y + height <= 278) return y;
    doc.addPage();
    return addPageHeader(doc, data, title);
  };

  const drawMetricBox = (doc, x, y, width, label, value) => {
    doc.setFillColor(247, 247, 244);
    doc.setDrawColor(216, 216, 210);
    doc.roundedRect(x, y, width, 17, 2, 2, 'FD');
    setFont(doc, 'normal', 7, [100, 100, 100]);
    doc.text(pdfText(label).toUpperCase(), x + 3, y + 5);
    setFont(doc, 'bold', 10);
    const lines = doc.splitTextToSize(pdfText(value), width - 6).slice(0, 2);
    doc.text(lines, x + 3, y + 11);
  };

  const drawOverallPlan = (doc, data, x, y, width, height) => {
    const bounds = getBounds(data.polygon);
    const drawingWidth = Math.max(1, bounds.maxX - bounds.minX);
    const drawingHeight = Math.max(1, bounds.maxY - bounds.minY);
    const innerPadding = 9;
    const scale = Math.min(
      (width - innerPadding * 2) / drawingWidth,
      (height - innerPadding * 2) / drawingHeight
    );
    const offsetX = x + (width - drawingWidth * scale) / 2 - bounds.minX * scale;
    const offsetY = y + (height - drawingHeight * scale) / 2 - bounds.minY * scale;
    const mapPoint = (point) => ({
      x: Number(point.x) * scale + offsetX,
      y: Number(point.y) * scale + offsetY
    });

    doc.setFillColor(250, 250, 248);
    doc.setDrawColor(216, 216, 210);
    doc.roundedRect(x, y, width, height, 2, 2, 'FD');

    const mapped = data.polygon.map(mapPoint);
    const start = mapped[0];
    const deltas = mapped.slice(1).map((point, index) => [
      point.x - mapped[index].x,
      point.y - mapped[index].y
    ]);
    deltas.push([start.x - mapped[mapped.length - 1].x, start.y - mapped[mapped.length - 1].y]);

    doc.setFillColor(238, 220, 210);
    doc.setDrawColor(166, 77, 38);
    doc.setLineWidth(1.8);
    doc.lines(deltas, start.x, start.y, [1, 1], 'FD', true);

    data.runs.forEach((run) => {
      const runStart = mapPoint(run.start);
      const runEnd = mapPoint(run.end);
      const horizontal = Math.abs(runStart.y - runEnd.y) < 0.01;
      const midX = (runStart.x + runEnd.x) / 2 + (horizontal ? 0 : 6);
      const midY = (runStart.y + runEnd.y) / 2 + (horizontal ? -5 : 0);

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(32, 32, 32);
      doc.setLineWidth(0.45);
      doc.circle(midX, midY, 3.4, 'FD');
      setFont(doc, 'bold', 7);
      doc.text(String(run.index + 1), midX, midY + 1.1, { align: 'center' });
      setFont(doc, 'bold', 7);
      doc.text(`${run.lengthMm}mm`, midX, midY + 6.2, { align: 'center' });
    });

    data.corners.forEach((corner) => {
      const point = mapPoint(corner.point);
      if (corner.external) doc.setFillColor(180, 59, 45);
      else doc.setFillColor(49, 95, 136);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.7);
      doc.circle(point.x, point.y, 2.5, 'FD');
      setFont(doc, 'bold', 5.5, [255, 255, 255]);
      doc.text(corner.external ? 'E' : 'I', point.x, point.y + 0.9, { align: 'center' });
    });
  };

  const drawRunCard = (doc, data, run, y) => {
    const cardHeight = 27;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(216, 216, 210);
    doc.roundedRect(14, y, 182, cardHeight, 2, 2, 'FD');

    setFont(doc, 'bold', 9);
    doc.text(`Run ${run.index + 1}`, 18, y + 6);
    setFont(doc, 'normal', 8, [100, 100, 100]);
    doc.text(`${run.lengthMm}mm overall`, 192, y + 6, { align: 'right' });

    const barX = 18;
    const barY = y + 10;
    const barWidth = 174;
    const barHeight = 8;
    let cursorX = barX;
    const joinPositions = [];

    run.panels.forEach((panelLength, index) => {
      const segmentWidth = barWidth * (Number(panelLength) / Number(run.lengthMm));
      doc.setFillColor(index % 2 === 0 ? 245 : 238, index % 2 === 0 ? 245 : 238, index % 2 === 0 ? 242 : 235);
      doc.setDrawColor(32, 32, 32);
      doc.setLineWidth(0.4);
      doc.rect(cursorX, barY, segmentWidth, barHeight, 'FD');
      setFont(doc, 'bold', segmentWidth > 24 ? 6.5 : 5.5);
      doc.text(`${panelLength}mm`, cursorX + segmentWidth / 2, barY + 5.1, { align: 'center' });

      if (index < run.panels.length - 1) joinPositions.push(cursorX + segmentWidth);
      cursorX += segmentWidth;
    });

    joinPositions.forEach((joinX) => {
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(32, 32, 32);
      doc.circle(joinX, barY + barHeight / 2, 2.8, 'FD');
      setFont(doc, 'bold', 4.5);
      doc.text('SJ', joinX, barY + barHeight / 2 + 0.8, { align: 'center' });
    });

    setFont(doc, 'normal', 7, [100, 100, 100]);
    const sequence = run.panels.map((length) => `${length}mm panel`).join(' + ');
    doc.text(pdfText(sequence), 18, y + 23);
    return y + cardHeight + 4;
  };

  const drawBom = (doc, data, y) => {
    const rowHeight = 8;
    const requiredHeight = 8 + rowHeight + data.bom.length * rowHeight + 13;
    y = ensureSpace(doc, data, y, requiredHeight, 'Assembly details');
    y = drawSectionHeading(doc, 'Component list', y);

    const columns = { item: 14, qty: 156, subtotal: 196 };
    doc.setFillColor(247, 247, 244);
    doc.rect(14, y, 182, rowHeight, 'F');
    setFont(doc, 'bold', 8);
    doc.text('Component', columns.item + 2, y + 5.3);
    doc.text('Qty', columns.qty, y + 5.3, { align: 'right' });
    doc.text('Subtotal', columns.subtotal - 2, y + 5.3, { align: 'right' });
    y += rowHeight;

    data.bom.forEach((row) => {
      y = ensureSpace(doc, data, y, rowHeight + 2, 'Component list');
      doc.setDrawColor(225, 225, 220);
      doc.line(14, y + rowHeight, 196, y + rowHeight);
      setFont(doc, 'normal', 8);
      const label = doc.splitTextToSize(pdfText(row.label), 125)[0];
      doc.text(label, columns.item + 2, y + 5.3);
      doc.text(String(row.quantity), columns.qty, y + 5.3, { align: 'right' });
      const subtotal = row.subtotalCents === null
        ? 'Set in cart'
        : formatMoney(row.subtotalCents, data.currency);
      doc.text(pdfText(subtotal), columns.subtotal - 2, y + 5.3, { align: 'right' });
      y += rowHeight;
    });

    y = ensureSpace(doc, data, y, 11, 'Component list');
    doc.setDrawColor(32, 32, 32);
    doc.setLineWidth(0.6);
    doc.line(14, y, 196, y);
    setFont(doc, 'bold', 10);
    doc.text('Estimated total', 16, y + 7);
    const total = data.hasUnknownPrice
      ? 'Final price calculated in cart'
      : formatMoney(data.totalCents, data.currency);
    doc.text(pdfText(total), 194, y + 7, { align: 'right' });
    return y + 13;
  };

  const addFooters = (doc, data) => {
    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setDrawColor(220, 220, 215);
      doc.line(14, 286, 196, 286);
      setFont(doc, 'normal', 7, [105, 105, 105]);
      const reference = pdfText(data.projectReference || 'Modular planter configuration');
      doc.text(reference, 14, 291);
      doc.text(`Page ${page} of ${pageCount}`, 196, 291, { align: 'right' });
    }
  };

  const createPdfDocument = (data) => {
    const JsPdf = requireJsPdf();
    const doc = new JsPdf({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
      putOnlyUsedFonts: true
    });

    const projectName = data.projectReference || 'Modular planter configuration';
    const date = new Date(data.generatedAt || Date.now());
    const generatedDate = Number.isNaN(date.getTime()) ? '' : date.toLocaleString('en-NZ');
    const total = data.hasUnknownPrice
      ? 'Final price calculated in cart'
      : formatMoney(data.totalCents, data.currency);

    doc.setProperties({
      title: `${pdfText(projectName)} - assembly details`,
      subject: 'Modular planter assembly details',
      author: pdfText(data.shopName || ''),
      creator: 'Modular Planter Builder'
    });

    let y = 14;
    setFont(doc, 'normal', 9, [100, 100, 100]);
    doc.text(pdfText(data.shopName || ''), 14, y);
    y += 8;
    setFont(doc, 'bold', 21);
    doc.text(pdfText(data.assemblyHeading || 'Modular planter assembly details'), 14, y);
    y += 7;
    setFont(doc, 'bold', 11);
    doc.text(pdfText(projectName), 14, y);
    y += 5;
    setFont(doc, 'normal', 7.5, [100, 100, 100]);
    doc.text(`Generated ${pdfText(generatedDate)}`, 14, y);
    y += 7;

    const gap = 3;
    const metricWidth = (182 - gap * 2) / 3;
    const metrics = [
      ['Starting shape', data.startingShape],
      ['Perimeter', `${(Number(data.perimeterMm) / 1000).toFixed(1).replace('.0', '')} m`],
      ['Footprint', `${(Number(data.areaSquareMm) / 1000000).toFixed(2)} m2`],
      ['External brackets', data.externalCorners],
      ['Straight joins', data.straightJoins],
      ['Estimated total', total]
    ];
    metrics.forEach((metric, index) => {
      const column = index % 3;
      const row = Math.floor(index / 3);
      drawMetricBox(doc, 14 + column * (metricWidth + gap), y + row * 20, metricWidth, metric[0], metric[1]);
    });
    y += 41;

    y = drawSectionHeading(doc, 'Overall planter layout', y);
    drawOverallPlan(doc, data, 14, y, 182, 94);
    y += 99;

    setFont(doc, 'normal', 7, [90, 90, 90]);
    doc.setFillColor(180, 59, 45);
    doc.circle(16, y, 2.1, 'F');
    setFont(doc, 'bold', 5, [255, 255, 255]);
    doc.text('E', 16, y + 0.8, { align: 'center' });
    setFont(doc, 'normal', 7, [90, 90, 90]);
    doc.text('External corner bracket', 20, y + 0.8);
    doc.setFillColor(49, 95, 136);
    doc.circle(70, y, 2.1, 'F');
    setFont(doc, 'bold', 5, [255, 255, 255]);
    doc.text('I', 70, y + 0.8, { align: 'center' });
    setFont(doc, 'normal', 7, [90, 90, 90]);
    doc.text('Internal corner - no bracket', 74, y + 0.8);
    doc.setDrawColor(32, 32, 32);
    doc.circle(145, y, 2.1, 'S');
    setFont(doc, 'bold', 4.3);
    doc.text('SJ', 145, y + 0.7, { align: 'center' });
    setFont(doc, 'normal', 7, [90, 90, 90]);
    doc.text('Straight join bracket', 149, y + 0.8);

    doc.addPage();
    y = addPageHeader(doc, data, 'Wall-run layouts');
    data.runs.forEach((run) => {
      y = ensureSpace(doc, data, y, 31, 'Wall-run layouts');
      y = drawRunCard(doc, data, run, y);
    });

    y = drawBom(doc, data, y + 2);

    if (data.assemblyNotes) {
      y = ensureSpace(doc, data, y, 28, 'Assembly notes');
      y = drawSectionHeading(doc, 'Assembly notes', y);
      const noteLines = doc.splitTextToSize(pdfText(data.assemblyNotes), 174);
      const noteHeight = Math.max(18, noteLines.length * 4.5 + 8);
      y = ensureSpace(doc, data, y, noteHeight, 'Assembly notes');
      doc.setFillColor(247, 247, 244);
      doc.setDrawColor(166, 77, 38);
      doc.setLineWidth(1.2);
      doc.rect(14, y, 182, noteHeight, 'F');
      doc.line(14, y, 14, y + noteHeight);
      setFont(doc, 'normal', 8);
      doc.text(noteLines, 19, y + 6);
      y += noteHeight + 5;
    }

    y = ensureSpace(doc, data, y, 18, 'Important');
    setFont(doc, 'bold', 8);
    doc.text('Important', 14, y);
    setFont(doc, 'normal', 7.5, [85, 85, 85]);
    const disclaimer = doc.splitTextToSize(
      'Verify all dimensions, panel orientation, site conditions and footing requirements before manufacture or installation.',
      182
    );
    doc.text(disclaimer, 14, y + 5);

    addFooters(doc, data);
    return doc;
  };

  const createPdfFile = (data) => {
    const doc = createPdfDocument(data);
    const projectPart = safeFilename(data.projectReference || data.startingShape || 'modular-planter');
    const filename = `${projectPart}-assembly-details.pdf`;
    const blob = doc.output('blob');
    const file = typeof File === 'function'
      ? new File([blob], filename, { type: 'application/pdf', lastModified: Date.now() })
      : null;
    return { doc, filename, blob, file };
  };

  const downloadFile = ({ blob, filename }) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.hidden = true;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  const makeEmailBody = (data) => {
    const lines = [
      data.assemblyHeading || 'Modular planter assembly details',
      '',
      `Project: ${data.projectReference || 'Not specified'}`,
      `Starting shape: ${data.startingShape}`,
      `Perimeter: ${(Number(data.perimeterMm) / 1000).toFixed(1).replace('.0', '')}m`,
      `External corner brackets: ${data.externalCorners}`,
      `Straight join brackets: ${data.straightJoins}`,
      '',
      'Wall runs:'
    ];
    data.runs.forEach((run) => {
      lines.push(`Run ${run.index + 1}: ${run.lengthMm}mm - ${run.panels.join(' + ')}mm`);
    });
    lines.push('', 'Components:');
    data.bom.forEach((row) => lines.push(`${row.quantity} x ${row.label}`));
    if (data.assemblyNotes) lines.push('', 'Assembly notes:', data.assemblyNotes);
    lines.push('', 'A PDF assembly pack can be downloaded from the planter builder and attached to this email.');
    return lines.join('\n');
  };

  const setStatus = (root, message, type = '') => {
    const element = root.querySelector('[data-assembly-status]');
    if (!element) return;
    element.textContent = message || '';
    element.dataset.type = type;
  };

  const getData = (root) => {
    if (typeof root.mpbGetAssemblyData !== 'function') return null;
    try {
      return root.mpbGetAssemblyData();
    } catch (error) {
      console.error('Modular planter assembly data error:', error);
      return null;
    }
  };

  const initialiseRoot = (root) => {
    if (!root || root.dataset.assemblyInitialised === 'true') return;
    const downloadButton = root.querySelector('[data-assembly-download]');
    const shareButton = root.querySelector('[data-assembly-share]');
    if (!downloadButton || !shareButton) return;

    root.dataset.assemblyInitialised = 'true';

    const updateButtons = () => {
      const ready = Boolean(getData(root));
      const libraryReady = Boolean(window.jspdf?.jsPDF);
      downloadButton.disabled = !ready || !libraryReady;
      shareButton.disabled = !ready || !libraryReady;

      if (!libraryReady) {
        setStatus(root, 'The PDF library could not be loaded. Check that jspdf.umd.min.js is installed in Assets.', 'error');
      } else if (!ready) {
        setStatus(root, 'Complete and close the planter shape to create the assembly PDF.');
      } else if (root.querySelector('[data-assembly-status]')?.dataset.type !== 'success') {
        setStatus(root, 'Assembly PDF is ready.');
      }
    };

    root.addEventListener('mpb:statechange', updateButtons);
    root.querySelector('[data-project-reference]')?.addEventListener('input', updateButtons);

    downloadButton.addEventListener('click', () => {
      const data = getData(root);
      if (!data) {
        updateButtons();
        return;
      }
      try {
        downloadFile(createPdfFile(data));
        setStatus(root, 'Assembly PDF downloaded.', 'success');
      } catch (error) {
        console.error('Modular planter assembly PDF error:', error);
        setStatus(root, 'The assembly PDF could not be created. Check that both assembly assets are installed.', 'error');
      }
    });

    shareButton.addEventListener('click', async () => {
      const data = getData(root);
      if (!data) {
        updateButtons();
        return;
      }

      try {
        const prepared = createPdfFile(data);
        const canShareFiles = Boolean(
          prepared.file &&
          navigator.share &&
          (!navigator.canShare || navigator.canShare({ files: [prepared.file] }))
        );

        if (canShareFiles) {
          await navigator.share({
            title: `${data.projectReference || 'Modular planter'} assembly details`,
            text: 'Modular planter layout and assembly details.',
            files: [prepared.file]
          });
          setStatus(root, 'Assembly PDF shared.', 'success');
          return;
        }

        const subject = `${data.projectReference || 'Modular planter'} assembly details`;
        const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(makeEmailBody(data))}`;
        window.location.href = mailto;
        setStatus(root, 'Your email application should open. Download the PDF and attach it manually if required.', 'success');
      } catch (error) {
        if (error?.name === 'AbortError') {
          setStatus(root, 'Sharing cancelled.');
          return;
        }
        console.error('Modular planter assembly share error:', error);
        setStatus(root, 'Sharing is not available in this browser. Download the PDF instead.', 'error');
      }
    });

    updateButtons();
  };

  const scan = (scope = document) => {
    if (scope.matches?.('[id^="ModularPlanterBuilder-"]')) initialiseRoot(scope);
    scope.querySelectorAll?.('[id^="ModularPlanterBuilder-"]').forEach(initialiseRoot);
  };

  const api = {
    scan,
    version: 2,
    createPdfDocument,
    createPdfFile
  };
  window[GLOBAL_KEY] = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
